import React, { useEffect, useRef, useState } from "react";
import { Mail, Play, Flag } from "lucide-react";
import api from "../api";
import { toast } from "../hooks/use-toast";

export const VideoMessageReveal = ({ code, url, from, brand = "#16243b", autoOpen = false }) => {
  const [reporting, setReporting] = useState(false);
  const [reason, setReason] = useState("");
  const [reported, setReported] = useState(false);
  const report = async () => {
    try {
      await api.post(`/voucher/${code}/report-video`, { reason });
      setReported(true); setReporting(false);
      toast({ title: "Thanks for letting us know", description: "The business has been alerted to review this video." });
    } catch { toast({ title: "Could not send report", variant: "destructive" }); }
  };
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const boxRef = useRef(null);
  useEffect(() => {
    if (autoOpen) boxRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [autoOpen]);
  const reveal = () => {
    setOpen(true);
    setTimeout(() => ref.current?.play().catch(() => {}), 450);
  };
  const src = `${process.env.REACT_APP_BACKEND_URL}${url}`;
  return (
    <div ref={boxRef} className="mb-6" data-testid="video-message-reveal">
      {!open ? (
        <button onClick={reveal} data-testid="video-envelope-btn"
          className="group relative w-full overflow-hidden rounded-3xl p-8 text-left text-white shadow-[0_20px_50px_rgba(20,30,55,0.18)] transition-transform hover:-translate-y-0.5"
          style={{ background: brand }}>
          <div className="absolute -right-6 -top-6 h-32 w-32 rotate-12 rounded-2xl bg-white/10 transition-transform duration-500 group-hover:rotate-45" />
          <Mail size={34} className="relative" />
          <p className="relative mt-4 text-xl font-bold">You have a video message!</p>
          <p className="relative text-sm opacity-80 mt-1">{from ? `From ${from}` : "Someone recorded something special for you"}</p>
          <span className="relative mt-5 inline-flex items-center gap-2 rounded-full bg-white text-[#16243b] px-5 py-2 text-sm font-semibold"><Play size={14} /> Open & watch</span>
        </button>
      ) : (
        <div className="rounded-3xl overflow-hidden bg-black shadow-[0_20px_50px_rgba(20,30,55,0.18)] animate-[fadeIn_.45s_ease-out]">
          <video ref={ref} src={src} controls playsInline className="w-full max-h-[70vh]" data-testid="video-message-player" />
        </div>
      )}
      {open && (
        <div className="mt-2 text-right">
          {reported ? <span className="text-xs text-[#8a97a8]" data-testid="video-reported">Reported — thank you.</span> : !reporting ? (
            <button onClick={() => setReporting(true)} data-testid="video-report-btn" className="text-xs text-[#8a97a8] hover:text-red-500 inline-flex items-center gap-1"><Flag size={12} /> Report this video</button>
          ) : (
            <div className="bg-white rounded-xl p-3 shadow-sm text-left" data-testid="video-report-form">
              <p className="text-sm font-medium text-[#16243b] mb-2">What's wrong with this video?</p>
              <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} maxLength={300} placeholder="Optional details" data-testid="video-report-reason"
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-red-300 resize-none" />
              <div className="flex justify-end gap-2 mt-2">
                <button onClick={() => setReporting(false)} className="text-sm text-[#5a6b80] px-3 py-1.5">Cancel</button>
                <button onClick={report} data-testid="video-report-submit" className="text-sm bg-red-500 text-white rounded-lg px-3 py-1.5">Send report</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
